import { useMemo } from 'react';

import formatDate from '@/utils/formatDate.ts';

interface PatientStatusTooltipProps {
  patientStatus?: string | null;
  statusReason?: string | null;
  statusStartDate?: string | Date | null;
  statusEndDate?: string | Date | null;
  statusUpdatedAt?: string | Date | null;
}

const usePatientStatusTooltip = (patient: PatientStatusTooltipProps | null) =>
  useMemo(() => {
    if (!patient) return { shouldShow: false, items: [] };

    const { patientStatus, statusReason, statusStartDate, statusEndDate, statusUpdatedAt } =
      patient;

    const shouldHide =
      (patientStatus === 'Inactive' && !statusUpdatedAt && !statusReason) ||
      (patientStatus === 'Active' && !statusUpdatedAt) ||
      (patientStatus === 'Paused' && !statusReason && !statusStartDate);

    if (shouldHide) return { shouldShow: false, items: [] };

    const items = (() => {
      switch (patientStatus) {
        case 'Inactive':
          return statusReason ? [{ label: 'Reason', value: statusReason }] : [];
        case 'Active':
          return [{ label: 'Active Date', value: formatDate(statusUpdatedAt) }];
        case 'Paused': {
          const pauseItems = [];
          if (statusStartDate) {
            pauseItems.push({
              label: 'Pause Date',
              value: `${formatDate(statusStartDate)} until ${formatDate(statusEndDate)}`
            });
          }
          if (statusReason) {
            pauseItems.push({ label: 'Reason', value: statusReason });
          }
          return pauseItems;
        }
        default:
          return [];
      }
    })();

    return { shouldShow: true, items };
  }, [patient]);

export default usePatientStatusTooltip;
